import {UtilsLayer} from "./UtilsLayer";

export class IOManagerLayer extends UtilsLayer {
    // Singleton instance
    private static instance: IOManagerLayer;

    // Map to hold elements with the id as the key
    protected elementsMap: Map<string, JQuery> = new Map();
    protected isInteractable: boolean = true;
    protected isVisible: boolean = true;
    protected style: string = "";
    protected elementStates: Map<string, any> = new Map();
    // A map to track the number of clones for each element
    protected cloneCounters: Map<string, number> = new Map();

    protected static getInstance(): IOManagerLayer {
        if (!IOManagerLayer.instance) {
            IOManagerLayer.instance = new IOManagerLayer();
        }
        return IOManagerLayer.instance;
    }

    private checkUniqueId(e: JQuery): boolean {
        const id = e.attr("id");
        if (id) {
            if (this.elementsMap.has(id)) {
                console.error(`Element with id ${id} already exists in the map.`);
                return false;  // ID is not unique
            } else {
                console.log("ID is unique");
                return true;  // ID is unique
            }
        } else {
            console.error("Element does not have an ID");
            return false;
        }
    }

    protected addElement(e: JQuery) {
        const id = e.attr("id");
        if (id && this.checkUniqueId(e)) {  // Only add if the ID is unique
            this.elementsMap.set(id, e);
            console.log(`Element with id ${id} added successfully.`);
        } else {
            console.error("Failed to add element. It either has no ID or the ID is not unique.");
        }
    }

    protected setIsInteractable(elementId: string, isInteractable: boolean) {
        const element = this.elementsMap.get(elementId);
        if (element) {
            element.prop('disabled', !isInteractable);
        } else {
            console.error(`Element with id ${elementId} not found`);
        }
    }

    protected setVisibility(elementId: string, isVisible: boolean) {
        const element = this.elementsMap.get(elementId);
        if (element) {
            element.toggle(isVisible);
        } else {
            console.error(`Element with id ${elementId} not found`);
        }
    }


    protected setElementStyle(elementId: string, style: string) {
        const element = this.elementsMap.get(elementId);
        if (element) {
            element.attr("style", style);
        } else {
            console.error(`Element with id ${elementId} not found`);
        }
    }


    protected removeElement(elementId: string) {
        if (this.elementsMap.has(elementId)) {
            this.elementsMap.delete(elementId);
        } else {
            console.error(`Element with id ${elementId} not found`);
        }
    }

    protected addEventListener(elementId: string, eventType: string, callback: (e: JQuery.Event) => void) {
        const element = this.elementsMap.get(elementId);
        if (element) {
            element.on(eventType, callback);
        } else {
            console.error(`Element with id ${elementId} not found`);
        }
    }

    protected removeEventListener(elementId: string, eventType: string) {
        const element = this.elementsMap.get(elementId);
        if (element) {
            element.off(eventType);
        } else {
            console.error(`Element with id ${elementId} not found`);
        }
    }

    protected setElementState(elementId: string, state: any) {
        this.elementStates.set(elementId, state);
    }

    public getElementState(elementId: string): any {
        return this.elementStates.get(elementId);
    }

    protected addClass(elementId: string, className: string) {
        const element = this.elementsMap.get(elementId);
        if (element) {
            element.addClass(className);
        } else {
            console.error(`Element with id ${elementId} not found`);
        }
    }

    protected removeClass(elementId: string, className: string) {
        const element = this.elementsMap.get(elementId);
        if (element) {
            element.removeClass(className);
        } else {
            console.error(`Element with id ${elementId} not found`);
        }
    }

    protected toggleClass(elementId: string, className: string) {
        const element = this.elementsMap.get(elementId);
        if (element) {
            element.toggleClass(className);
        } else {
            console.error(`Element with id ${elementId} not found`);
        }
    }

    protected updateTextContent(elementId: string, text: string) {
        const element = this.elementsMap.get(elementId);
        if (element) {
            element.text(text);
        } else {
            console.error(`Element with id ${elementId} not found`);
        }
    }

    protected updateInputValue(elementId: string, value: string) {
        const element = this.elementsMap.get(elementId);
        if (element && element.is("input, textarea")) {
            element.val(value);
        } else {
            console.error(`Element with id ${elementId} not found or is not an input element`);
        }
    }

    protected cloneElement(elementId: string): JQuery | null {
        const element = this.elementsMap.get(elementId);
        if (element) {
            // Increment the clone counter for this element ID or initialize it if not yet tracked
            const cloneCount = (this.cloneCounters.get(elementId) || 0) + 1;
            this.cloneCounters.set(elementId, cloneCount);

            // Clone the element
            const clone = element.clone();

            // Create a new unique ID by appending the clone number
            const newId = `${elementId}_${cloneCount}`;
            clone.attr("id", newId);  // Set the new ID on the cloned element

            // Add the cloned element to the map
            this.addElement(clone);

            return clone;
        } else {
            console.error(`Element with id ${elementId} not found`);
            return null;
        }
    }
}